import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Backdrop,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { 
  updateUser,
  getEmployeeTypes, 
  getDesignations, 
  getRoles, 
  getLevels,
  getCountries,
} from "../../../Action/api";

const EditPersonDialog = ({ personData, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Dropdown options from API
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [levels, setLevels] = useState([]);
  const [countries, setCountries] = useState([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false);

  const [formData, setFormData] = useState({
    // Core employee info (read-only)
    uId: "",
    userId: "",
    depId: "",
    depName: "",
    roleId: "",
    roleName: "",
    
    // Basic editable info
    staffFullName: "",
    personalEmail: "",
    contactNumber: "",
    gender: "",
    maritalStatus: "",
    dateOfBirth: "",
    nationality: "",
    governmentId: "",
    bloodGroup: "",
    
    // Address info
    currentAddress: "",
    permanentAddress: "",
    
    // Employment details (some read-only)
    levelId: "",
    levelName: "",
    grade: "",
    desId: "",
    desName: "",
    empTypeId: "",
    joiningDate: "",
    workLocation: "",
    probationPeriod: "",
    reportingMngId: "",
    
    // Personal details
    hobbies: "",
    yourPassion: "",
    shortTermGoal: "",
    longTermGoal: "",
    primaryLanguage: "",
    knownLanguage: "",
    
    // Leave allocation
    allocatedAnnualLeave: 0,
    allocatedSickLeave: 0,
    allocatedEmergencyLeave: 0,
    allocatedUnpaidLeave: 0,
    allocatedMaternityLeave: 0,
  });

  // Load dropdown data
  useEffect(() => {
    const loadDropdownData = async () => {
      setIsLoadingDropdowns(true);
      try {
        const [empTypes, desigs, rolesData, levelsData, countriesData] = await Promise.all([
          getEmployeeTypes(),
          getDesignations(),
          getRoles(),
          getLevels(),
          getCountries(),
        ]);
        
        console.log("Countries data from API:", countriesData);
        
        setEmployeeTypes(empTypes || []);
        setDesignations(desigs || []);
        setRoles(rolesData || []);
        setLevels(levelsData || []);
        setCountries(countriesData || []);
      } catch (error) {
        console.error("Error loading dropdown data:", error);
      } finally {
        setIsLoadingDropdowns(false);
      }
    };

    if (open) {
      loadDropdownData();
    }
  }, [open]);

  // Populate form with person data
  useEffect(() => {
    if (personData && open) {
      setFormData({
        uId: personData.uId || personData.staff_id || "",
        userId: personData.login_id || "",
        depId: personData.dep_id || "",
        depName: personData.dep_name || "",
        roleId: personData.roleId || "",
        roleName: personData.role_name || "",
        staffFullName: personData.name || "",
        personalEmail: personData.per_email || "",
        contactNumber: personData.contactNumber || "",
        gender: personData.gender || "",
        maritalStatus: personData.maritalStatus || "",
        dateOfBirth: personData.dateOfBirth || "",
        nationality: personData.res_country || "",
        governmentId: personData.governmentId || "",
        bloodGroup: personData.bloodGroup || "",
        currentAddress: personData.currentAddress || "",
        permanentAddress: personData.permanentAddress || "",
        levelId: personData.level_id || "",
        levelName: personData.level_name || "",
        grade: personData.grade || personData.grade_id || "",
        desId: personData.desId || "",
        desName: personData.desName || "",
        empTypeId: personData.empTypeId || "",
        joiningDate: personData.joiningDate || "",
        workLocation: personData.workLocation || "",
        probationPeriod: personData.probationPeriod || "",
        reportingMngId: personData.reportingMngId || "",
        hobbies: personData.hobbies || "",
        yourPassion: personData.passion || "",
        shortTermGoal: personData.shortTermGoal || "",
        longTermGoal: personData.longTermGoal || "",
        primaryLanguage: personData.primaryLanguage || "",
        knownLanguage: personData.knownLanguage || "",
        allocatedAnnualLeave: personData.allocatedAnnualLeave || 0,
        allocatedSickLeave: personData.allocatedSickLeave || 0,
        allocatedEmergencyLeave: personData.allocatedEmergencyLeave || 0,
        allocatedUnpaidLeave: personData.allocatedUnpaidLeave || 0,
        allocatedMaternityLeave: personData.allocatedMaternityLeave || 0,
      });
      setError(null);
      setSuccess(false);
    }
  }, [personData, open]);

  const handleChange = (field, value) => {
    // Validate leave allocation fields
    const leaveFields = [
      'allocatedAnnualLeave',
      'allocatedSickLeave', 
      'allocatedEmergencyLeave',
      'allocatedUnpaidLeave',
      'allocatedMaternityLeave'
    ];
    
    if (leaveFields.includes(field)) {
      // Allow empty string while typing
      if (value === '' || value === null || value === undefined) {
        value = '';
      } else {
        const numValue = parseInt(value);
        // Ensure value is between 1 and 20
        if (isNaN(numValue) || numValue < 1) {
          value = 1;
        } else if (numValue > 20) {
          value = 20;
        } else {
          value = numValue;
        }
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Prepare data for API
      const updateData = {
        ...formData,
        // Ensure required fields are included
        uId: formData.uId,
        userId: formData.userId,
      };

      const response = await updateUser(updateData);
      
      console.log("Update successful:", response);
      setSuccess(true);
      
      // Auto-close dialog and refresh data after 1.5 seconds
      setTimeout(() => {
        setLoading(false);
        if (onSuccess) {
          onSuccess();
        }
      }, 1500);
    } catch (err) {
      console.error("Error updating user:", err);
      setError(err.message || "Failed to update user");
      setLoading(false);
    }
  };

  if (!personData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#228B22",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Edit Person: {personData.name}</Typography>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {isLoadingDropdowns && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Person updated successfully!
          </Alert>
        )}

        {!isLoadingDropdowns && (
          <Grid container spacing={3}>
            {/* Read-only Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: "#228B22", mb: 2 }}>
                Employee Information (Read-only)
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Employee ID"
                value={formData.uId}
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: "#f5f5f5" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="User ID"
                value={formData.userId}
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: "#f5f5f5" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Department ID"
                value={formData.depId}
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: "#f5f5f5" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Role ID"
                value={formData.roleId}
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: "#f5f5f5" }}
                helperText="Role cannot be modified"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department Name"
                value={formData.depName}
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: "#f5f5f5" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Role Name"
                value={formData.roleName}
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: "#f5f5f5" }}
              />
            </Grid>

            {/* Basic Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: "#228B22", mb: 2 }}>
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.staffFullName}
                onChange={(e) => handleChange("staffFullName", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Personal Email"
                type="email"
                value={formData.personalEmail}
                onChange={(e) => handleChange("personalEmail", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                value={formData.contactNumber}
                onChange={(e) => handleChange("contactNumber", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  onChange={(e) => handleChange("gender", e.target.value)}
                  label="Gender"
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Marital Status</InputLabel>
                <Select
                  value={formData.maritalStatus}
                  onChange={(e) => handleChange("maritalStatus", e.target.value)}
                  label="Marital Status"
                >
                  <MenuItem value="Single">Single</MenuItem>
                  <MenuItem value="Married">Married</MenuItem>
                  <MenuItem value="Divorced">Divorced</MenuItem>
                  <MenuItem value="Widowed">Widowed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleChange("dateOfBirth", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {countries.length > 0 ? (
                <FormControl fullWidth>
                  <InputLabel>Nationality</InputLabel>
                  <Select
                    value={formData.nationality}
                    onChange={(e) => handleChange("nationality", e.target.value)}
                    label="Nationality"
                  >
                    <MenuItem value="">
                      <em>Select Nationality</em>
                    </MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.countryCode || country.cid} value={country.countryName || country.cname}>
                        {country.countryName || country.cname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label="Nationality"
                  value={formData.nationality}
                  onChange={(e) => handleChange("nationality", e.target.value)}
                  helperText={isLoadingDropdowns ? "Loading countries..." : "Enter nationality"}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Government ID"
                value={formData.governmentId}
                onChange={(e) => handleChange("governmentId", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Blood Group</InputLabel>
                <Select
                  value={formData.bloodGroup}
                  onChange={(e) => handleChange("bloodGroup", e.target.value)}
                  label="Blood Group"
                >
                  <MenuItem value="A+">A+</MenuItem>
                  <MenuItem value="A-">A-</MenuItem>
                  <MenuItem value="B+">B+</MenuItem>
                  <MenuItem value="B-">B-</MenuItem>
                  <MenuItem value="AB+">AB+</MenuItem>
                  <MenuItem value="AB-">AB-</MenuItem>
                  <MenuItem value="O+">O+</MenuItem>
                  <MenuItem value="O-">O-</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: "#228B22", mb: 2 }}>
                Address Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Current Address"
                multiline
                rows={2}
                value={formData.currentAddress}
                onChange={(e) => handleChange("currentAddress", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Permanent Address"
                multiline
                rows={2}
                value={formData.permanentAddress}
                onChange={(e) => handleChange("permanentAddress", e.target.value)}
              />
            </Grid>

            {/* Employment Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: "#228B22", mb: 2 }}>
                Employment Details
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Level</InputLabel>
                <Select
                  value={formData.levelId}
                  onChange={(e) => {
                    const selectedLevel = levels.find(level => level.levelId === e.target.value);
                    handleChange("levelId", e.target.value);
                    if (selectedLevel) {
                      handleChange("levelName", selectedLevel.levelName);
                    }
                  }}
                  label="Level"
                >
                  {levels
                    .filter((level) => 
                      level.levelName !== "Owner/Founder" && 
                      level.levelName !== "Owner"
                    )
                    .map((level) => (
                      <MenuItem key={level.levelId} value={level.levelId}>
                        {level.levelName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Grade"
                value={formData.grade}
                onChange={(e) => handleChange("grade", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Designation</InputLabel>
                <Select
                  value={formData.desId}
                  onChange={(e) => {
                    const selectedDes = designations.find(des => des.desId === e.target.value);
                    handleChange("desId", e.target.value);
                    if (selectedDes) {
                      handleChange("desName", selectedDes.desName);
                    }
                  }}
                  label="Designation"
                >
                  {designations.map((des) => (
                    <MenuItem key={des.desId} value={des.desId}>
                      {des.desName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={formData.empTypeId}
                  onChange={(e) => handleChange("empTypeId", e.target.value)}
                  label="Employment Type"
                >
                  {employeeTypes.map((type) => (
                    <MenuItem key={type.empTypeId} value={type.empTypeId}>
                      {type.empTypeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Joining Date"
                type="date"
                value={formData.joiningDate}
                onChange={(e) => handleChange("joiningDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Work Location"
                value={formData.workLocation}
                onChange={(e) => handleChange("workLocation", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Probation Period</InputLabel>
                <Select
                  value={formData.probationPeriod}
                  onChange={(e) => handleChange("probationPeriod", e.target.value)}
                  label="Probation Period"
                >
                  <MenuItem value="1 Month">1 Month</MenuItem>
                  <MenuItem value="2 Month">2 Month</MenuItem>
                  <MenuItem value="3 Month">3 Month</MenuItem>
                  <MenuItem value="6 Month">6 Month</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Reporting Manager ID"
                value={formData.reportingMngId}
                onChange={(e) => handleChange("reportingMngId", e.target.value)}
              />
            </Grid>

            {/* Personal Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: "#228B22", mb: 2 }}>
                Personal Details
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hobbies"
                value={formData.hobbies}
                onChange={(e) => handleChange("hobbies", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Your Passion"
                value={formData.yourPassion}
                onChange={(e) => handleChange("yourPassion", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Short Term Goal"
                multiline
                rows={2}
                value={formData.shortTermGoal}
                onChange={(e) => handleChange("shortTermGoal", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Long Term Goal"
                multiline
                rows={2}
                value={formData.longTermGoal}
                onChange={(e) => handleChange("longTermGoal", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primary Language"
                value={formData.primaryLanguage}
                onChange={(e) => handleChange("primaryLanguage", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Known Languages"
                value={formData.knownLanguage}
                onChange={(e) => handleChange("knownLanguage", e.target.value)}
              />
            </Grid>

            {/* Leave Allocation */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: "#228B22", mb: 2 }}>
                Leave Allocation
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Annual Leave"
                type="number"
                value={formData.allocatedAnnualLeave}
                onChange={(e) => handleChange("allocatedAnnualLeave", e.target.value)}
                onKeyDown={(e) => {
                  // Prevent special characters, only allow numbers and control keys
                  if (!/^[0-9]$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    handleChange("allocatedAnnualLeave", 1);
                  }
                }}
                inputProps={{ min: 1, max: 20 }}
                helperText="Range: 1-20 days"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Sick Leave"
                type="number"
                value={formData.allocatedSickLeave}
                onChange={(e) => handleChange("allocatedSickLeave", e.target.value)}
                onKeyDown={(e) => {
                  // Prevent special characters, only allow numbers and control keys
                  if (!/^[0-9]$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    handleChange("allocatedSickLeave", 1);
                  }
                }}
                inputProps={{ min: 1, max: 20 }}
                helperText="Range: 1-20 days"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Emergency Leave"
                type="number"
                value={formData.allocatedEmergencyLeave}
                onChange={(e) => handleChange("allocatedEmergencyLeave", e.target.value)}
                onKeyDown={(e) => {
                  // Prevent special characters, only allow numbers and control keys
                  if (!/^[0-9]$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    handleChange("allocatedEmergencyLeave", 1);
                  }
                }}
                inputProps={{ min: 1, max: 20 }}
                helperText="Range: 1-20 days"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Unpaid Leave"
                type="number"
                value={formData.allocatedUnpaidLeave}
                onChange={(e) => handleChange("allocatedUnpaidLeave", e.target.value)}
                onKeyDown={(e) => {
                  // Prevent special characters, only allow numbers and control keys
                  if (!/^[0-9]$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    handleChange("allocatedUnpaidLeave", 1);
                  }
                }}
                inputProps={{ min: 1, max: 20 }}
                helperText="Range: 1-20 days"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maternity Leave"
                type="number"
                value={formData.allocatedMaternityLeave}
                onChange={(e) => handleChange("allocatedMaternityLeave", e.target.value)}
                onKeyDown={(e) => {
                  // Prevent special characters, only allow numbers and control keys
                  if (!/^[0-9]$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    handleChange("allocatedMaternityLeave", 1);
                  }
                }}
                inputProps={{ min: 1, max: 20 }}
                helperText="Range: 1-20 days"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          disabled={loading || isLoadingDropdowns}
          sx={{
            backgroundColor: "#228B22",
            "&:hover": {
              backgroundColor: "#1b6b1b",
            },
          }}
        >
          {loading ? "Updating..." : "Update Person"}
        </Button>
      </DialogActions>

      {/* Amazing Loading Overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'absolute',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
        }}
        open={loading}
      >
        <Fade in={loading}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!success ? (
              <>
                <CircularProgress
                  size={80}
                  thickness={4}
                  sx={{
                    color: '#228B22',
                    mb: 3,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': {
                        opacity: 1,
                        transform: 'scale(1)',
                      },
                      '50%': {
                        opacity: 0.8,
                        transform: 'scale(1.1)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'scale(1)',
                      },
                    },
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    mb: 1,
                    animation: 'fadeIn 0.5s ease-in',
                    '@keyframes fadeIn': {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                    },
                  }}
                >
                  Updating Person...
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    animation: 'fadeIn 0.5s ease-in 0.2s both',
                  }}
                >
                  Please wait while we save the changes
                </Typography>
              </>
            ) : (
              <>
                <CheckCircleIcon
                  sx={{
                    fontSize: 100,
                    color: '#4caf50',
                    mb: 2,
                    animation: 'scaleIn 0.5s ease-out',
                    '@keyframes scaleIn': {
                      '0%': {
                        transform: 'scale(0)',
                        opacity: 0,
                      },
                      '50%': {
                        transform: 'scale(1.2)',
                      },
                      '100%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                    },
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    mb: 1,
                    animation: 'fadeIn 0.3s ease-in 0.2s both',
                  }}
                >
                  Success!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    animation: 'fadeIn 0.3s ease-in 0.4s both',
                  }}
                >
                  Person updated successfully
                </Typography>
              </>
            )}
          </Box>
        </Fade>
      </Backdrop>
    </Dialog>
  );
};

export default EditPersonDialog;
